import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import moment from 'moment';
import * as am4charts from '@amcharts/amcharts4/charts';
import { get, groupBy } from 'lodash';

import { IDeviceDataUsage } from '../../../../../../services/core/devicePerformance';
import { DeviceEvent, TzDeviceEvent } from '../../../../../../services/core/eventsTypes';
import { EditEvents, EditEventsAction, IEventFilterGroups, useEditEventsDispatch } from '../events/edit-events';
import { deviceEventFilterGroupNames, IDeviceEventFilterState } from '../events/deviceEventsTypes';
import { getDeviceEventFilterGroups } from '../events/deviceEvents';
import { EventsShown } from '../events-shown';
import { ChartName, chartNames } from './lib/chartNames';
import { useFeatureTogglesContext } from '../../../../../../context/featureToggles';

import { formatTooltipDayWithHourRange } from '@components/chart/tooltips';
import { StackedColumnsDateChart, IProps as IChartProps } from '@components/chart/stackedColumnsDateChart';
import { drawXAxisLabelsFromDateRange, formatDateAxisLabelsInTimeZone } from '@components/chart/lib';

import './charts.css';
import '../events/deviceEvents.css';
import { drawYAxis } from '@lib/dataUsageChart';

interface ICommonProps {
  classname: string,
  data: IDeviceDataUsage[],
  dateRange: { from: number, to: number },
  platformType: string,
  eventFilters: IDeviceEventFilterState,
  dispatchEventFilters: React.Dispatch<EditEventsAction<ChartName>>,
  visible: boolean,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  showApplyAll?: boolean,
  /** Overrides the chart's default "Data Usage Per Hour (Wi-Fi + Mobile)" header - e.g. with a
   * date/range suffix, matching DeviceChargeLevel's own title override convention. Left unset by
   * every performance-tab usage of this component, which keeps the generic header. */
  title?: string,
  /** Optional x-axis title (e.g. "Device Local Time (...)" - see DeviceChargeLevel's own
   * xAxisTitle convention). Left unset by every performance-tab usage of this component, which
   * has never labelled the axis - only set by callers (e.g. the data usage anomaly examination
   * page) that need to make the axis's time zone explicit. */
  xAxisTitle?: string
}

export type IProps = ICommonProps & (
  { displayTimeZone?: undefined, events: DeviceEvent[] } |
  { displayTimeZone: string, events: TzDeviceEvent[] }
);

export const useChartData = (props: IProps): { eventFilterGroups: IEventFilterGroups, chartProps: Pick<IChartProps<any>, "title" | "data" | "series" | "drawXAxis" | "drawYAxis"> } => {
  const { t } = useTranslation('performance');

  const featureToggles = useFeatureTogglesContext();

  const eventFilterGroups = getDeviceEventFilterGroups(props.platformType, featureToggles);

  const data = useMemo(() => {
    if (!props.data.length) {
      return [];
    }
    const eventsByHour = groupBy(props.events, (event) => moment.utc(
      props.displayTimeZone ? (event as TzDeviceEvent).utc : event.local
    ).startOf('hour').valueOf());
    return props.data.map(({ date, mobile, wifi }, i) => {
      return {
        date: moment.utc(parseInt(date)).valueOf(),
        tooltipDate: parseInt(date),
        mobile: mobile || 0,
        wifi: wifi || 0,
        events: get(eventsByHour, date, [])
      };
    });
  }, [props.data, props.events, props.displayTimeZone]);

  const series = React.useMemo(() => [
    {
      colour: '#49a1a9',
      dataKey: 'mobile',
      description: t('MOBILE_DATA_LINES'),
      visible: true,
    },
    {
      colour: '#2c678c',
      dataKey: 'wifi',
      description: t('WIFI_DATA_LINES'),
      visible: true,
    }
  ], [t]);

  return {
    eventFilterGroups,

    chartProps: {
      title: props.title ?? t('DATA_USAGE_HOUR_WIFI_MOBILE'),
      data,
      series,
      drawYAxis: (chart, axis, enabledSeries) => drawYAxis(chart.data, axis, enabledSeries),
      drawXAxis: (_, xAxis) => {
        drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, props.dateRange);
        formatDateAxisLabelsInTimeZone(xAxis as am4charts.DateAxis, props.displayTimeZone);
        if (props.xAxisTitle) {
          (xAxis as am4charts.DateAxis).title.text = props.xAxisTitle;
        }
      }
    }
  };
};

export function DataUsage24Hours(props: IProps) {
  const { classname, eventFilters, dispatchEventFilters, onMount, showApplyAll = true } = props;

  const { t } = useTranslation();

  const {
    handleUpdate, handleApply, handleApplyAll, handleCancel
  } = useEditEventsDispatch(
    dispatchEventFilters,
    chartNames.dataUsage24Hours,
    /* istanbul ignore next */
    () => {}
  );

  const { eventFilterGroups, chartProps } = useChartData(props);

  return (
    <div className={`core-device_performance-${classname}-data-usage`}>
      <StackedColumnsDateChart<any>
        {...chartProps}
        headerControls={<div className="chart-edit-events">
          <EventsShown eventFilters={eventFilters.appliedEventFilters} />
          <EditEvents
            subheader={`‘${chartProps.title}’`}
            eventGroupNames={deviceEventFilterGroupNames}
            eventFilterGroups={eventFilterGroups}
            eventFilterState={eventFilters.currentEventFilters}
            handleUpdate={handleUpdate}
            handleApply={handleApply}
            handleCancel={handleCancel}
            handleApplyAll={showApplyAll ? handleApplyAll : undefined}
          />
        </div>}
        cypressId="last24HoursOrSelectedDateDataUsage"
        hasLegend={true}
        days={1}
        yAxisType='value'
        amchartsYAxisNumberFormat="#.00b"
        xAxisType={'date'}
        useAxisTooltip
        totalDescription={t('TOTAL_DATA', { ns: 'translation' })}
        dateAxisEndDuration={{ amount: "1", unit: 'hour' }}
        formatTooltipDate={(date) => formatTooltipDayWithHourRange(date, props.displayTimeZone)}
        eventOverlay={true}
        displayTimeZone={props.displayTimeZone}
        onMount={onMount}
        adjustChartDimensions={{ height: 250 }}
      />
    </div>
  );
}
